import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending approval' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

/**
 * Template visibility / governance editor. Mirrors Alchemy's MediaItem gating:
 * only Published + in-date templates surface in the "use" view, and
 * branch/franchise + region/office/employee restrictions limit who sees them.
 */
export default function GovernancePanel({ visibility, onChange }) {
  const v = visibility || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const setIds = (key, str) =>
    set({ [key]: str.split(',').map((s) => s.trim()).filter(Boolean) });

  return (
    <div className="space-y-4" data-testid="governance-panel">
      <div className="space-y-1">
        <Label className="text-xs">Status</Label>
        <Select value={v.status || 'draft'} onValueChange={(s) => set({ status: s })}>
          <SelectTrigger data-testid="gov-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground">Only <strong>Published</strong> templates appear when agents choose a template.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Visible from</Label>
          <Input type="date" data-testid="gov-effective" value={v.effective_date || ''}
            onChange={(e) => set({ effective_date: e.target.value || null })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Expires</Label>
          <Input type="date" data-testid="gov-expiry" value={v.expiry_date || ''}
            onChange={(e) => set({ expiry_date: e.target.value || null })} />
        </div>
      </div>

      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox data-testid="gov-branch" checked={!!v.branch_only}
            onCheckedChange={(c) => set({ branch_only: !!c, franchise_only: c ? false : v.franchise_only })} />
          Branch offices only
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox data-testid="gov-franchise" checked={!!v.franchise_only}
            onCheckedChange={(c) => set({ franchise_only: !!c, branch_only: c ? false : v.branch_only })} />
          Franchise offices only
        </label>
      </div>

      <div className="space-y-2">
        {[
          ['restricted_regions', 'Restrict to regions (IDs)'],
          ['restricted_offices', 'Restrict to offices (IDs)'],
          ['restricted_employees', 'Restrict to agents (IDs)'],
        ].map(([key, label]) => (
          <div className="space-y-1" key={key}>
            <Label className="text-xs">{label}</Label>
            <Input data-testid={`gov-${key}`} value={(v[key] || []).join(', ')}
              onChange={(e) => setIds(key, e.target.value)} placeholder="blank = everyone" />
          </div>
        ))}
        <p className="text-[11px] text-muted-foreground">Leave blank for no restriction. A searchable region/office/agent picker is a follow-up.</p>
      </div>
    </div>
  );
}
